import isObject from '#shared/util/isObject.js';

import cBillingSummary from '#themes/default/default/components/billingSummary/billingSummary.js';
import cCardInput from '#themes/default/default/components/cardInput/cardInput.js';
import cContentLayout from '#themes/default/default/layouts/contentLayout/contentLayout.js';
import cCvcHint from '#themes/default/default/components/cvcHint/cvcHint.js';
import cDropdown from '#themes/default/default/components/dropdown/dropdown.js';
import cInfoBox from '#themes/default/default/components/infoBox/infoBox.js';
import cLegalAgreement from '#themes/default/default/components/legalAgreement/legalAgreement.js';
import cLegalAgreementCheckbox from '#themes/default/default/components/legalAgreementCheckbox/legalAgreementCheckbox.js';
import cProvidersElement from '#themes/default/default/components/providersElement/providersElement.js';
import cSvg from '#themes/default/default/components/svg/svg.js';
import cTooltip from '#themes/default/default/components/tooltip/tooltip.js';

import paymentFailureText from '#frontend/util/paymentFailureText.js';

import template from './paymentSelection.html' with { type: 'html' };

export default {
	name: 'CPaymentSelection',
	components: {
		cBillingSummary,
		cCardInput,
		cContentLayout,
		cCvcHint,
		cDropdown,
		cInfoBox,
		cLegalAgreement,
		cLegalAgreementCheckbox,
		cProvidersElement,
		cSvg,
		cTooltip
	},
	props: {
		utils: {
			type: Object,
			default: () => null
		}
	},
	data() {
		return {
			cardSecurityCodeHasError: false,
			isCvcEmpty: true,
			isCvcFocused: false,
			note: ''
		};
	},
	computed: {
		customer() {
			return this.$store.state.customer;
		},
		form() {
			return this.$store.state.form;
		},
		isCompany() {
			return this.$store.state.isCompany;
		},
		isEmpty() {
			return this.$store.state.isEmpty;
		},
		isLoading() {
			return this.$store.state.isLoading;
		},
		isNewMethod() {
			return this.$store.state.isNewMethod;
		},
		order() {
			return this.$store.state.order;
		},
		page() {
			return this.$store.state.page;
		},
		payment() {
			return this.$store.state.payment;
		},
		selectedPayment() {
			return this.$store.state.selectedPayment;
		},
		providerAccount() {
			return this.$store.state.providerAccount;
		},
		terms() {
			return this.$store.state.terms;
		},
		theme() {
			return this.$store.state.theme;
		},
		url() {
			return this.$store.state.url;
		},
		hasTermsCheckbox() {
			return this.$store.getters.hasTermsCheckbox;
		},
		showDisclaimer() {
			return this.$store.getters.showDisclaimer;
		},
		isShoppingCartPage() {
			return isObject(this.page) && this.page.number === 0;
		},
		isCreditCard() {
			return this.selectedPayment?.type === 'creditCard';
		},
		isBancontact() {
			return this.selectedPayment?.type === 'bancontact';
		},
		defaultPayment() {
			return (this.payment.providerAccountPaymentOptions || [])[0];
		},
		errorMessage() {
			const dropdownError = Array.isArray(this.payment.providerAccountPaymentOptions) && this.payment.providerAccountPaymentOptions.find((option) => option.failed && option.id === this.selectedPayment.id);
			if (this.cardSecurityCodeHasError && this.isCreditCard) {
				return this.t('form_errorCardSecurityCode');
			} else if (dropdownError) {
				return paymentFailureText(this.t, this.payment).subHeadline;
			} else {
				return null;
			}
		},
		oneClickNote() {
			return this.t('shoppingCart_oneClickInfoContentShort').replace(/<a.+?>|<\/a>/g, '');
		},
		has4digitCvc() {
			// Amex cards has 4-digit long CVC code
			const amex = ['amex', 'american-express', 'americanexpress'];
			return this.selectedPayment && amex.includes(this.selectedPayment.cardBrand) || this.payment && amex.includes(this.payment.slug);
		}
	},
	watch: {
		$route(to, from = {}) {
			// Clear security code field if shopping cart page will be loaded from different page
			if (to.name !== 'shopping-cart' || to.name === from.name) {
				return;
			}
			try {
				this.selectedPayment.cardcvv.clear();
			} catch {
				// Silence is golden
			}
		}
	},
	async mounted() {
		// Set all states to default to avoid unwanted situations with a new method submitted flow
		this.$store.commit('SET_FORM', {
			creditCard: null,
			bancontact: null,
			directDebit: null
		});
		// Apply default payment option
		if (this.isShoppingCartPage) {
			const defaultPaymentSelected = await this.waitUntil([() => this.defaultPayment], 60 * 1000);
			if (defaultPaymentSelected) {
				this.$store.commit('SET_SELECTED_PAYMENT', this.defaultPayment);
				if (this.payment.sourceId !== this.defaultPayment.sourceId) {
					await this.utils.applyPayment({
						paymentOptionId: this.defaultPayment.id,
						sourceString: this.defaultPayment.sourceClientSecret
					});
				}
			}
		}
	},
	methods: {
		setInputStatus({ data: { elementType, empty, error, complete }, name }) {
			if (name === 'blur' && this.isCvcEmpty || error) {
				this.changeInputStatus(elementType, true);
			}
			if (typeof empty !== 'undefined') {
				this.isCvcEmpty = elementType === 'cardcvv' ? empty : this.isCvcEmpty;
			}
			if (complete) {
				setTimeout(() => this.changeInputStatus(elementType, false), 0);
			}
		},
		changeInputStatus(elementType, hasError) {
			// Change credit card touched status to true after it's been interacted with
			if (!this.form.creditCard?.touched) {
				this.$store.commit('SET_FORM', {
					creditCard: {
						touched: true
					}
				});
			}
			if (elementType === 'cardcvv') {
				this.cardSecurityCodeHasError = hasError;
			}
		},
		async setSelectedPayment(paymentOption) {
			// If user clicked on already selected item, do nothing
			if (paymentOption.sourceId === this.selectedPayment.sourceId) {
				return;
			}
			this.utils.showLoader();
			// Change selected payment option
			this.$store.commit('SET_SELECTED_PAYMENT', paymentOption);
			// Clear cvc (if exists) input when you select a different method
			const cvcElement = this.selectedPayment.cardcvv;
			if (cvcElement) {
				cvcElement.clear();
			}
			// Hide errors from cvc field when changing payment options
			this.cardSecurityCodeHasError = false;
			// Set initial provider elements state to show loaders until ready
			this.$store.commit('SET_SELECTED_PAYMENT', {
				cardBrand: null,
				cardcvv: null,
				cardexpiration: null,
				cardnumber: null,
				iban: null
			});
			// Reset form so there are no visible errors when changing the payment methods
			this.$store.commit('SET_FORM', {
				creditCard: null,
				bancontact: null,
				directDebit: null
			});
			// Update provider library for the selected payment method (eg. change UI for creditCard or directDebit)
			this.providersLibrary.loadPayment();
			// Apply the selected payment method
			await this.utils.applyPayment({
				paymentOptionId: paymentOption.id,
				sourceString: paymentOption.sourceClientSecret
			});
			this.utils.hideLoader();
		},
		onBlur() {
			this.note = '';
			this.isCvcFocused = false;
		},
		onFocus() {
			this.note = this.has4digitCvc ? this.t('form_noteCvc4') : this.t('form_noteCvc3');
			this.isCvcFocused = true;
			// Unset used provider account payment option as failed (one-click)
			if (Array.isArray(this.payment.providerAccountPaymentOptions)) {
				const providerAccountPaymentOptions = this.payment.providerAccountPaymentOptions.map((paymentOption) => {
					if (paymentOption.failed) {
						paymentOption.failed = false;
					}
					return paymentOption;
				});
				this.$store.commit('SET_PAYMENT', {
					...this.payment,
					providerAccountPaymentOptions
				});
			}
		},

		/**
		 * Handle input "return" event and submit the form
		 * @todo We should refactor this method and use some "global" methods or new models to handle this
		 */
		onProvidersElementSubmit() {
			const submitButton = document.querySelector('.t-paymentInfo_submit');
			submitButton.click();
		}
	},
	template
};
